import { Dialog, Button } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTheme } from "../../../components/hooks/useTheme";
import colorConfigs from "../../../configs/colorConfigs";
import useAuth from "../../../components/hooks/useAuth";
import { securityDepositManuallyChange } from "../../../services/rental";
import Cookies from "js-cookie";
import useTenant from "../../../components/hooks/useTenant";
import useConfirm from "../../../components/hooks/useConfirm";

type Props = {
  openSecurityDepositDialog: boolean;
  setOpenSecurityDepositDialog: (value: boolean) => void;
  reservationId: number;
  securityDepositAmountToPaid: number
  updateSecurityDepositAmountToPaid: (updateAmount: number) => void;
  setWasSecurityDepositManuallyChanged: (value: boolean) => void;
  summaryData: any;
  refreshTheSummary: () => void;
  setPleaseWaitPopup: (value: boolean) => void;
};

export default function SecuityDeposit({
  openSecurityDepositDialog,
  setOpenSecurityDepositDialog,
  reservationId,
  securityDepositAmountToPaid,
  updateSecurityDepositAmountToPaid,
  setWasSecurityDepositManuallyChanged,
  summaryData,
  refreshTheSummary,
  setPleaseWaitPopup
}: Props) {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { auth }: any = useAuth();
  const { handleTenantError }: any = useTenant();
  const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
  const { theme, themeStyles } = useTheme();
  const [securityDepositAmount, setSecurityDepositAmount] = useState<number>(0.0);
  const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

  useEffect(() => {
    if (reservationId > 0) {
      setSecurityDepositAmount(securityDepositAmountToPaid);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityDepositAmountToPaid])

  useEffect(() => {
    setSecurityDepositAmount(summaryData?.securityDepositAmount ? summaryData?.securityDepositAmount : 0.0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryData])

  const updateReservationSecurityDeposit = async () => {
    if (isNaN(securityDepositAmount)) {
      setConfirmData({
        title: "Warning",
        summary: "The value entered is not a valid number.",
        confirmBtn: "Ok",
        isConfirm: (callback: any) => {
          callback();
        },
      });
      setConfirmPopup(true);
      return;
    }

    setPleaseWaitPopup(true);

    if (reservationId > 0) {
      securityDepositManuallyChange(
        securityDepositAmount,
        reservationId,
        auth?.id,
        tenantId
      )
        .then(() => {
          refreshTheSummary();
          updateSecurityDepositAmountToPaid(securityDepositAmount);
          setOpenSecurityDepositDialog(false);
          setWasSecurityDepositManuallyChanged(true);
          handleTenantError(null);
        })
        .catch(error => {
          setPleaseWaitPopup(false);
          handleTenantError(error);
        })
    } else {
      updateSecurityDepositAmountToPaid(securityDepositAmount);
      setOpenSecurityDepositDialog(false);
      setWasSecurityDepositManuallyChanged(true);
    }
  }


  return (
    <>
      <Dialog
        open={openSecurityDepositDialog}
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: '350px',
            width: '100%',
            borderRadius: '16px',
          },
        }}
      >
        <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 320.591 320.591"
            className={`w-3 cursor-pointer float-right ${closeIconFill}`}
            onClick={() => setOpenSecurityDepositDialog(false)}
          >
            <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
            <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
          </svg>

          <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
            <div className="w-full mt-2">
              <div className={`w-full`}>
                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                  <div className="flex items-center gap-1.5">
                    <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Edit Security Deposit</h2>
                  </div>
                </div>

                <div className="mt-2 p-2">
                  <div className="w-full">
                    <div>
                      <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Security Deposit</label>
                      <input
                        type="text"
                        className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                        value={securityDepositAmount}
                        onChange={(e) => setSecurityDepositAmount(Number(e.target.value))}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 mt-6">
                  <Button variant="contained"
                    sx={{
                      backgroundColor: colorConfigs.btn.main.bg
                    }}
                    onClick={() => updateReservationSecurityDeposit()}
                  >
                    Save
                  </Button>

                  <Button variant="contained"
                    sx={{
                      backgroundColor: colorConfigs.btn.cancel.bg,
                      border: colorConfigs.btn.cancel.border,
                      color: colorConfigs.btn.cancel.color
                    }}
                    onClick={() => setOpenSecurityDepositDialog(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
